import { RoundedBadge } from '@/components/rounded-badge';
import { RoundedButton } from '@/components/rounded-button';
import { toaster } from '@/components/ui/toaster';
import { dailySpinChainId } from '@/constants/shared';
import { useAuth } from '@/hooks/useAuth';
import { useCountdown } from '@/hooks/useCountdown';
import { RafflePrize, useRaffle } from '@/hooks/useRaffle';
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from '@/lib/amplitude';
import { RAFFLE_ABI } from '@/network/abi/raffle';
import { formatCurrencyToLocale, formatNumberToLocale, getImgPathFromPrizeId } from '@/utils';
import { Box, Card, Heading, HStack, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import { Config, readContract, simulateContract } from '@wagmi/core';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { IoCheckmark } from 'react-icons/io5';
import { Address } from 'viem';
import { useConfig, useWriteContract } from 'wagmi';
import { RafflePrizeCardSkeleton } from '../Skeletons';
import { AddTicketsModal } from './AddTicketsModal';

type CtaType = 'addTickets' | 'claim' | undefined;
export const RafflePrizesList = ({
  rafflePrizes,
  isLoading,
  isLoadingDailySpinOffchain,
  numTicketsToSubmit,
  setNumTicketsToSubmit,
  raffleTicketsBalance,
  placeholder,
  onRefresh,
  isAuthenticated,
  ctaType,
  showImgQuantity = true,
  setShowConfetti,
}: {
  rafflePrizes: RafflePrize[];
  isLoading: boolean;
  numTicketsToSubmit: number;
  setNumTicketsToSubmit: (n: number) => void;
  raffleTicketsBalance: number;
  placeholder: string;
  onRefresh: () => void;
  isAuthenticated: boolean;
  isLoadingDailySpinOffchain: boolean;
  ctaType?: CtaType;
  showImgQuantity?: boolean;
  setShowConfetti?: (s: boolean) => void;
}) => {
  if (isLoading) return <RafflePrizeCardSkeleton />;
  if (rafflePrizes.length)
    return (
      <>
        {rafflePrizes.map((rafflePrize) => (
          <RafflePrizeCard
            key={rafflePrize.id}
            rafflePrize={rafflePrize}
            prizeId={rafflePrize.id}
            ticketsAvailable={raffleTicketsBalance}
            numTicketsToSubmit={numTicketsToSubmit}
            setNumTicketsToSubmit={setNumTicketsToSubmit}
            onRefresh={onRefresh}
            isAuthenticated={isAuthenticated}
            isLoadingUserTickets={isLoadingDailySpinOffchain}
            ctaType={ctaType}
            showImgQuantity={showImgQuantity}
            setShowConfetti={setShowConfetti}
          />
        ))}
      </>
    );
  return placeholder;
};

const getWinnerIndex = async (wagmiConfig: Config, prizeId: number, address: Address | undefined) => {
  try {
    if (!address) throw new Error('No eth address specified');
    const winnerIndicesData = await readContract(wagmiConfig, {
      address: import.meta.env.VITE_RAFFLE_CONTRACT_ADDRESS,
      abi: RAFFLE_ABI,
      chainId: dailySpinChainId,
      functionName: 'getWinnerIndices',
      args: [BigInt(prizeId), true, address],
    });
    return winnerIndicesData?.[0];
  } catch (e) {
    console.error('Error retrieving winner index', e);
    toaster.create({
      type: 'error',
      title: 'Cannot claim prize',
      description: `Cannot claim prize, ${e}`,
    });
    return;
  }
};

export const RafflePrizeCard = ({
  rafflePrize,
  prizeId,
  ticketsAvailable,
  numTicketsToSubmit,
  setNumTicketsToSubmit,
  onRefresh,
  isAuthenticated,
  isLoadingUserTickets,
  ctaType,
  showImgQuantity,
  setShowConfetti,
}: {
  rafflePrize: RafflePrize;
  prizeId: number;
  ticketsAvailable: number;
  numTicketsToSubmit: number;
  setNumTicketsToSubmit: (n: number) => void;
  onRefresh: () => void;
  isAuthenticated: boolean;
  isLoadingUserTickets: boolean;
  ctaType: CtaType;
  showImgQuantity?: boolean;
  setShowConfetti?: (s: boolean) => void;
}) => {
  const { name, description, value, totalTickets: tickets, yourTickets, endsAt } = rafflePrize;
  const ended = endsAt ? endsAt.getTime() < Date.now() : false;
  const wagmiConfig = useConfig();
  const { address } = useAuth();
  const { refetchRaffle } = useRaffle();
  const { writeContractAsync, isPending } = useWriteContract();
  const [isClaimingPrize, setIsClaimingPrize] = useState(false);
  const claimPrize = useCallback(async () => {
    const winnerIndex = await getWinnerIndex(wagmiConfig, prizeId, address);
    if (winnerIndex === undefined) {
      return;
    }
    try {
      trackAmplitudeEvent(AmplitudeTrackingEvent.RafflePrizeClaimed, {
        prize_id: prizeId,
      });
      await simulateContract(wagmiConfig, {
        abi: RAFFLE_ABI,
        address: import.meta.env.VITE_RAFFLE_CONTRACT_ADDRESS,
        functionName: 'claimPrize',
        chainId: dailySpinChainId,
        args: [BigInt(prizeId), winnerIndex],
      });
      await writeContractAsync({
        abi: RAFFLE_ABI,
        address: import.meta.env.VITE_RAFFLE_CONTRACT_ADDRESS,
        functionName: 'claimPrize',
        chainId: dailySpinChainId,
        args: [BigInt(prizeId), winnerIndex],
      });
      setShowConfetti?.(true);
      refetchRaffle();
    } catch (e) {
      toaster.create({
        type: 'error',
        title: 'Cannot claim prize',
        description: `Error claiming prize: ${e}`,
      });
    } finally {
      setIsClaimingPrize(false);
    }
  }, [address, prizeId, refetchRaffle, setShowConfetti, wagmiConfig, writeContractAsync]);

  const onClaimClick = useCallback(async () => {
    setIsClaimingPrize(true);

    await Tally.openPopup(rafflePrize.formId, {
      layout: 'modal',
      onSubmit: claimPrize,
      onClose: () => setIsClaimingPrize(false),
    });
  }, [claimPrize, rafflePrize.formId]);

  const cta = useMemo(() => {
    if (ctaType === 'addTickets') {
      return (
        <AddTicketsModal
          name={name}
          prizeId={prizeId}
          numOfTicketsToSubmit={numTicketsToSubmit}
          setNumOfTicketsToSubmit={setNumTicketsToSubmit}
          ticketsAvailable={ticketsAvailable}
          onSuccess={() => {
            onRefresh();
          }}
          fullWidth
        />
      );
    }
    if (ctaType === 'claim') {
      if (rafflePrize.claimed) {
        return (
          <RoundedBadge bgColor="#00827B1A" color="#00827B" h={10} px={4}>
            <IoCheckmark /> Claimed
          </RoundedBadge>
        );
      }
      return (
        <RoundedButton onClick={onClaimClick} variant="solid" w="full" loading={isPending || isClaimingPrize}>
          Claim
        </RoundedButton>
      );
    }
    return null;
  }, [
    ctaType,
    isClaimingPrize,
    isPending,
    name,
    numTicketsToSubmit,
    onClaimClick,
    onRefresh,
    prizeId,
    rafflePrize.claimed,
    setNumTicketsToSubmit,
    ticketsAvailable,
  ]);
  const { hours, minutes, seconds } = useCountdown(endsAt);

  return (
    <>
      <Card.Root w="full" borderRadius="8px" overflow="hidden" hideFrom="md">
        <Card.Body p={6}>
          <VStack alignItems="start" gap={4}>
            <HStack gap={4}>
              <Box
                w="90px"
                rounded="16px"
                h="90px"
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
                bg="white"
                flexShrink={0}
              >
                <Image
                  src={getImgPathFromPrizeId(prizeId, showImgQuantity ? 'withQuantity' : 'plain')}
                  w="100%"
                  h="100%"
                  objectFit="contain"
                />
              </Box>
              <Heading fontSize="lg">{name}</Heading>
            </HStack>
            <Text fontWeight="500" color="text.secondary">
              {description}
            </Text>
            <VStack alignItems="start" gap={3} w="full">
              <RaffleStatSmall title="Value" value={formatCurrencyToLocale(value).replace('.00', '')} />

              <RaffleStatSmall title="Tickets" value={formatNumberToLocale(tickets)} />

              {isAuthenticated && (
                <RaffleStatSmall title="Your Tickets" value={formatNumberToLocale(yourTickets || 0)} />
              )}

              {endsAt && !ended && <RaffleStatSmall title="Ends At" value={endsAt.toLocaleDateString()} />}

              {isAuthenticated && <Box w="full">{cta}</Box>}
            </VStack>
          </VStack>
        </Card.Body>
      </Card.Root>
      <Card.Root w="full" borderRadius="2xl" overflow="hidden" hideBelow="md">
        <HStack h="full" gap={0}>
          <Box
            w="226px"
            h="226px"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            bg="white"
            flexShrink={0}
          >
            <Image
              src={getImgPathFromPrizeId(prizeId, showImgQuantity ? 'withQuantity' : 'plain')}
              w="100%"
              h="100%"
              objectFit="contain"
            />
          </Box>
          <VStack alignSelf="stretch" w="full">
            <HStack
              alignSelf="stretch"
              justifyContent="space-between"
              bg="bg"
              flex="1"
              padding={6}
              borderBottom="1px solid #F0F0F0"
            >
              <Box>
                <Heading size="md">{name}</Heading>
                <Text color="fg.muted" fontWeight="500" maxW={['100%', '100%', '80%']}>
                  {description}
                </Text>
              </Box>
              {isAuthenticated && <Box>{cta}</Box>}
            </HStack>
            <HStack alignSelf="stretch" justifyContent="space-between" flex="1" padding={6}>
              <HStack flex="1" justifyContent="start">
                <RaffleStat title="Value" value={formatCurrencyToLocale(value).replace('.00', '')} />
                <RaffleStat title="Tickets" value={formatNumberToLocale(tickets)} />
                {isAuthenticated && (
                  <RaffleStat
                    title="Your Tickets"
                    value={formatNumberToLocale(yourTickets || 0)}
                    loading={isLoadingUserTickets}
                  />
                )}

                {endsAt && !ended && <RaffleStat title="Ending In" value={`${hours}:${minutes}:${seconds}`} />}
              </HStack>
              {endsAt && (
                <RoundedBadge h={9} bgColor={ended ? 'bg.muted' : '#FFF6C7'} color={ended ? 'fg.muted' : '#963D01'}>
                  {ended ? `Picked on ${endsAt.toLocaleDateString()}` : 'Ending Soon!'}
                </RoundedBadge>
              )}
            </HStack>
          </VStack>
        </HStack>
      </Card.Root>
    </>
  );
};

const RaffleStatSmall = ({ title, value }: { title: string; value: number | string | ReactElement }) => (
  <HStack justifyContent="space-between" w="full">
    <Text fontWeight="500" color="text.secondary">
      {title}
    </Text>
    <Heading fontSize="lg" fontWeight="500">
      {value}
    </Heading>
  </HStack>
);

const RaffleStat = ({
  title,
  value,
  loading = false,
}: {
  title: string;
  value: number | string | ReactElement;
  loading?: boolean;
}) => (
  <Box maxW={150} flex="1">
    <Text color="fg.muted" fontWeight="500" textWrap="nowrap">
      {title}
    </Text>
    {loading ? (
      <Spinner />
    ) : (
      <Heading size="md" fontWeight="500">
        {value}
      </Heading>
    )}
  </Box>
);
